import React, { useEffect, useState } from 'react';

/**
 * csToolsEnums.Events.MOUSE_MOVE, as a literal. extensions/sycai does not depend on
 * @cornerstonejs/tools, and declaring it -- even as a peer -- writes a new specifier into
 * pnpm-lock.yaml's importers section, which this milestone's lockfile constraint forbids for
 * the sake of one constant. develop listened on the same literal
 * (CustomizableViewportOverlay.tsx:185 there), and it is verified equal to the enum member in
 * the installed package.
 */
const MOUSE_MOVE = 'CORNERSTONE_TOOLS_MOUSE_MOVE';

export interface CursorCoordinatesItemProps {
  element?: HTMLElement;
  instance?: { Modality?: string };
  customization?: { color?: string };
}

/**
 * D6, and this is the load-bearing constraint of the milestone. _renderOverlayItem calls
 * `contentF` as a PLAIN FUNCTION and inserts the return as a React child
 * (CustomizableViewportOverlay.tsx:195), so a hook called inside contentF would run inside
 * CustomizableViewportOverlay's own render and violate the rules of hooks. Returning an
 * element makes React MOUNT this, which is what lets it hold the listener and its own state.
 * Nothing in the syntax reveals this.
 *
 * That also CONTAINS develop's unthrottled whole-overlay re-render per mousemove (§6): the
 * setState below re-renders this component, not the four corners. It does not FIX it.
 *
 * D10 -- replayed verbatim, defects included, confirmed with the user 2026-08-13:
 *   - canvas coordinates in CSS pixels, Math.round, no units. Not world, not image, not HU.
 *   - the CT gate lives here rather than in `condition`, as develop had it.
 *   - NO mouseleave / MOUSE_EXIT handler, so the last values stay frozen on screen after the
 *     pointer leaves the viewport, and NO throttle.
 * The freeze and the missing throttle are recorded in this milestone's carried-forward.md for
 * the whole-branch review rather than fixed, so that any behavioural difference in M4.2 is
 * attributable to the migration and not to an unrequested product change.
 */
export default function CursorCoordinatesItem({
  element,
  instance,
  customization,
}: CursorCoordinatesItemProps) {
  const [coordinates, setCoordinates] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!element) {
      return undefined;
    }

    const update = (event: Event) => {
      const currentPoints = (event as CustomEvent).detail?.currentPoints;

      if (!currentPoints?.canvas) {
        return;
      }

      setCoordinates({
        x: Math.round(currentPoints.canvas[0]),
        y: Math.round(currentPoints.canvas[1]),
      });
    };

    element.addEventListener(MOUSE_MOVE, update);

    return () => {
      element.removeEventListener(MOUSE_MOVE, update);
    };
  }, [element]);

  if (!coordinates || instance?.Modality !== 'CT') {
    return null;
  }

  return (
    <div
      className="overlay-item flex flex-row"
      style={{ color: customization?.color }}
    >
      <span className="mr-1 shrink-0">X:</span>
      <span className="ml-1 mr-2 shrink-0">{coordinates.x}</span>
      <span className="mr-1 shrink-0">Y:</span>
      <span className="ml-1 shrink-0">{coordinates.y}</span>
    </div>
  );
}
